import type { FC } from 'react';
import React from 'react';
import cn from 'classnames';
import { formatISODateToString } from '@alfa-bank/site-invest-common/helpers/date/format-iso-date-to-string';

import { findScore, getRatingDynamicType } from '../../helpers';
import {
    TOTAL_PREVIOUS_SCORE_TYPE_NAME,
    TOTAL_SCORE_TYPE_NAME,
    ALL_NEEDED_SCORE_TYPE_NAMES,
    RATING_TITLE,
} from '../../constants';
import type { TRatingDynamic } from '../../models';

import type { TAssetDecomposedRating } from './models';
import { ScorePlaceIcon } from './score-place-icon';
import { ScoreDynamicIcon } from './score-dynamic-icon';
import { isNotAllNeededScoresExist } from './helpers';

export const AssetDecomposedRating: FC<TAssetDecomposedRating> = ({
    Block,
    Grid,
    Hint,
    HintContent,
    Pentagram,
    Text,
    dataTestId,
    hintLink,
    scores,
    styles,
    title = RATING_TITLE,
    tooltipText,
}) => {
    if (isNotAllNeededScoresExist(scores, ALL_NEEDED_SCORE_TYPE_NAMES)) {
        return null;
    }
    const scorePlace = findScore(TOTAL_SCORE_TYPE_NAME, scores)?.place;
    const currentRatingValue = findScore(TOTAL_SCORE_TYPE_NAME, scores)?.value;
    const previousRating = findScore(TOTAL_PREVIOUS_SCORE_TYPE_NAME, scores);
    const previousRatingValue = previousRating?.value;
    const previousRatingCreatedDate = previousRating?.createdAt;
    let ratingDynamicType: TRatingDynamic = 'unchanged';

    if (currentRatingValue && previousRatingValue) {
        ratingDynamicType = getRatingDynamicType(currentRatingValue, previousRatingValue);
    }

    return (
        <Block className={cn(styles.external, styles.wrapper)} dataTestId={dataTestId} notPadded>
            <Grid
                alignment="center"
                colAutoWidth
                columnsMargin={8}
                justify="start"
                styler={{ marginBottom: 24 }}
            >
                <Text styler={{ marginTop: 0, marginBottom: 4 }} view="small">
                    {title}
                </Text>
                <Hint
                    content={<HintContent linkUrl={hintLink} text={tooltipText} title={title} />}
                    hasActionButton={!hintLink}
                    iconClassName={cn(styles.external, styles.hintIcon)}
                />
            </Grid>
            <Grid alignment="center" className={cn(styles.external, styles.pentagon)} colAutoWidth>
                <Pentagram scores={scores} />
            </Grid>
            {scorePlace && (
                <Block
                    className={cn(styles.external, styles.info)}
                    notPadded
                    styler={{ borderRadius: 24, padding: 12 }}
                >
                    <Grid
                        alignment="center"
                        className={cn(styles.external, styles.infoWrapper)}
                        colAutoWidth
                        columnsMargin={12}
                        justify="start"
                    >
                        <ScorePlaceIcon />
                        <Block notPadded>
                            <Text styler={{ margin: 0 }} view="action-component-primary">
                                {`${scorePlace} место`}
                            </Text>
                            <Text styler={{ margin: 0, marginTop: 4 }} view="primary-small">
                                из 80 оценённых акций в IMOEX
                            </Text>
                        </Block>
                    </Grid>
                </Block>
            )}
            {!!currentRatingValue && !!previousRatingValue && (
                <Block
                    className={cn(styles.external, styles.info)}
                    notPadded
                    styler={{ borderRadius: 24, padding: 12 }}
                >
                    <Grid
                        alignment="center"
                        className={cn(styles.external, styles.infoWrapper)}
                        colAutoWidth
                        columnsMargin={12}
                        justify="start"
                    >
                        <ScoreDynamicIcon dynamic={ratingDynamicType} />
                        <Block notPadded>
                            <Text styler={{ margin: 0 }} view="action-component-primary">
                                {ratingDynamicType === 'unchanged' ? (
                                    currentRatingValue
                                ) : (
                                    <>
                                        {previousRatingValue}
                                        &nbsp;&rarr;&nbsp;
                                        {currentRatingValue}
                                    </>
                                )}
                            </Text>
                            {previousRatingCreatedDate && (
                                <Text styler={{ margin: 0 }} view="primary-small">
                                    {`Предыдущая оценка ${formatISODateToString(
                                        previousRatingCreatedDate
                                    )}`}
                                </Text>
                            )}
                        </Block>
                    </Grid>
                </Block>
            )}
        </Block>
    );
};
